/**
 * IP Access Control Editor component for configuring per-port IP rules.
 *
 * @module components/ui/IpAccessControlEditor
 */

import { Plus, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { TagInput } from './TagInput';
import type { IpAccessControlAction, PortIpAccessControlRule } from '@/types';

export interface IpAccessControlEditorProps {
  /** The default action when no rules match */
  defaultAction: IpAccessControlAction;
  /** Callback when the default action changes */
  onDefaultActionChange: (action: IpAccessControlAction) => void;
  /** The list of IP access control rules */
  rules: PortIpAccessControlRule[];
  /** Callback when rules change */
  onRulesChange: (rules: PortIpAccessControlRule[]) => void;
}

const MAX_RULES = 10;
const MAX_CIDRS_PER_RULE = 10;

const CIDR_REGEX = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

function validateCidr(value: string): string | null {
  if (!CIDR_REGEX.test(value)) {
    return 'Must be a valid CIDR (e.g., 10.0.0.0/8)';
  }
  return null;
}

/**
 * A reusable component for editing IP access control settings.
 * Includes a default action selector and a dynamic list of IP rules.
 */
export function IpAccessControlEditor({
  defaultAction,
  onDefaultActionChange,
  rules,
  onRulesChange,
}: IpAccessControlEditorProps) {
  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) return;
    const nextPriority = rules.length > 0 ? Math.max(...rules.map((r) => r.priority)) + 100 : 100;
    onRulesChange([
      ...rules,
      { name: '', action: 'Allow', priority: nextPriority, sourceCidrs: [] },
    ]);
  };

  const handleRemoveRule = (index: number) => {
    onRulesChange(rules.filter((_, i) => i !== index));
  };

  const handleRuleFieldChange = (
    index: number,
    field: 'name' | 'action' | 'priority',
    value: string
  ) => {
    onRulesChange(
      rules.map((rule, i) => {
        if (i !== index) return rule;
        if (field === 'name') return { ...rule, name: value };
        if (field === 'action') return { ...rule, action: value as IpAccessControlAction };
        if (field === 'priority') return { ...rule, priority: parseInt(value) || 0 };
        return rule;
      })
    );
  };

  const handleCidrsChange = (index: number, cidrs: string[]) => {
    onRulesChange(rules.map((rule, i) => (i === index ? { ...rule, sourceCidrs: cidrs } : rule)));
  };

  return (
    <div className="space-y-6">
      <Select
        label="Default Action"
        value={defaultAction}
        onChange={(e) => onDefaultActionChange(e.target.value as IpAccessControlAction)}
        options={[
          { value: 'Allow', label: 'Allow - Allow all IPs by default' },
          { value: 'Deny', label: 'Deny - Block all IPs by default' },
        ]}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">IP Rules</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Add optional rules to allow or deny specific IP ranges.
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={handleAddRule}
            disabled={rules.length >= MAX_RULES}
          >
            Add Rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No rules. The default action will apply to all IP addresses.
          </p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
                {/* Rule header with remove button - always visible */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Rule {index + 1}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleRemoveRule(index)}
                    title="Remove rule"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Input
                      label="Name"
                      placeholder="rule-name"
                      value={rule.name}
                      onChange={(e) => handleRuleFieldChange(index, 'name', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Select
                      label="Action"
                      value={rule.action}
                      onChange={(e) => handleRuleFieldChange(index, 'action', e.target.value)}
                      options={[
                        { value: 'Allow', label: 'Allow' },
                        { value: 'Deny', label: 'Deny' },
                      ]}
                    />
                  </div>
                  <div>
                    <Input
                      label="Priority"
                      type="number"
                      placeholder="100"
                      value={String(rule.priority)}
                      onChange={(e) => handleRuleFieldChange(index, 'priority', e.target.value)}
                      min={0}
                      max={1000}
                      required
                    />
                  </div>
                </div>
                <TagInput
                  label="Source CIDRs"
                  value={rule.sourceCidrs}
                  onChange={(cidrs) => handleCidrsChange(index, cidrs)}
                  placeholder="10.0.0.0/8"
                  helperText={
                    rule.sourceCidrs.length >= MAX_CIDRS_PER_RULE
                      ? `Maximum ${MAX_CIDRS_PER_RULE} CIDRs per rule`
                      : 'Enter CIDR ranges (e.g., 192.168.1.0/24)'
                  }
                  disabled={rule.sourceCidrs.length >= MAX_CIDRS_PER_RULE}
                  validate={validateCidr}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
